import React, { useRef } from 'react';
import { useReaderState } from '../../hooks/useReaderState';
import { useKeyboardNav } from '../../hooks/useKeyboardNav';
import { useSwipeNav } from '../../hooks/useSwipeNav';
import { getActs, getChapters } from '../../data/tome';
import { PageSlot } from './PageSlot';
import { ReaderControls } from './ReaderControls';
import { TableOfContents } from './TableOfContents';
import { BookmarkDrawer } from './BookmarkDrawer';
import { BacklogDrawer } from './BacklogDrawer';
import { SettingsModal } from './SettingsModal';

export const VirtualizedReader: React.FC = () => {
  const {
    state,
    currentPageData,
    transitionDirection,
    gotoPage,
    nextPage,
    prevPage,
    stepPages,
    toggleToc,
    toggleBookmarks,
    toggleBacklog,
    toggleSettings,
    closeAllDrawers,
    addBookmark,
    removeBookmark,
    isCurrentPageBookmarked,
    updateSettings,
    toggleMute,
    clearBacklog,
    resetAllData,
  } = useReaderState();

  const acts = getActs();
  const chapters = getChapters();

  const viewportRef = useRef<HTMLElement>(null);

  const isAnyDrawerOpen =
    state.isTocOpen || state.isBookmarksOpen || state.isBacklogOpen || state.isSettingsOpen;

  // Multi-modal Keyboard Navigation
  useKeyboardNav({
    onNext: nextPage,
    onPrev: prevPage,
    onHome: () => gotoPage(1),
    onEnd: () => gotoPage(state.totalPages),
    onToggleToc: () => toggleToc(),
    onToggleBookmarks: () => toggleBookmarks(),
    onToggleBacklog: () => toggleBacklog(),
    onToggleSettings: () => toggleSettings(),
    onCloseAll: closeAllDrawers,
    isAnyDrawerOpen,
    enabled: true,
  });

  // Touch Swipe Navigation for mobile & tablet
  useSwipeNav({
    onSwipeLeft: nextPage,
    onSwipeRight: prevPage,
    threshold: 45,
    enabled: !isAnyDrawerOpen,
  });

  const progressFraction = (state.currentPage - 1) / (state.totalPages - 1);
  const progressPercent = (progressFraction * 100).toFixed(2);

  const transitionClass =
    transitionDirection === 'next'
      ? 'page-transition-next'
      : transitionDirection === 'prev'
      ? 'page-transition-prev'
      : 'page-transition-jump';

  return (
    <div
      className={`caesura-reader-shell theme-${state.settings.theme.replace(/_/g, '-')}`}
      data-testid="caesura-reader"
    >
      {/* Top Global Progress Line */}
      <div
        className="top-progress-indicator"
        style={{ width: `${progressPercent}%` }}
        role="progressbar"
        aria-valuenow={state.currentPage}
        aria-valuemin={1}
        aria-valuemax={state.totalPages}
      />

      {/* Main Viewport Container (Mounts single active page card) */}
      <main className="caesura-reader-viewport" ref={viewportRef}>
        <div
          key={currentPageData.pageNumber}
          className={`caesura-page-slot-container ${transitionClass}`}
        >
          <PageSlot
            pageData={currentPageData}
            settings={state.settings}
            isBookmarked={isCurrentPageBookmarked}
            onToggleBookmark={() => {
              if (isCurrentPageBookmarked) {
                const bm = state.bookmarks.find((b) => b.pageNumber === state.currentPage);
                if (bm) removeBookmark(bm.id);
              } else {
                addBookmark();
              }
            }}
          />
        </div>
      </main>

      {/* Bottom Sticky Control Bar */}
      <ReaderControls
        currentPage={state.currentPage}
        totalPages={state.totalPages}
        chapterTitle={currentPageData.chapterTitle}
        isMuted={state.isMuted}
        volume={state.volume}
        isCurrentPageBookmarked={isCurrentPageBookmarked}
        onPrev={prevPage}
        onNext={nextPage}
        onStep={stepPages}
        onGotoPage={gotoPage}
        onToggleToc={() => toggleToc()}
        onToggleBookmarks={() => toggleBookmarks()}
        onToggleBacklog={() => toggleBacklog()}
        onToggleSettings={() => toggleSettings()}
        onToggleMute={toggleMute}
        onToggleBookmarkCurrent={() => {
          if (isCurrentPageBookmarked) {
            const bm = state.bookmarks.find((b) => b.pageNumber === state.currentPage);
            if (bm) removeBookmark(bm.id);
          } else {
            addBookmark();
          }
        }}
      />

      {/* Slide-out Drawers & Modals */}
      <TableOfContents
        isOpen={state.isTocOpen}
        currentPage={state.currentPage}
        onClose={() => toggleToc(false)}
        onJumpToPage={gotoPage}
        acts={acts}
        chapters={chapters}
      />

      <BookmarkDrawer
        isOpen={state.isBookmarksOpen}
        currentPage={state.currentPage}
        bookmarks={state.bookmarks}
        isCurrentPageBookmarked={isCurrentPageBookmarked}
        onClose={() => toggleBookmarks(false)}
        onJumpToPage={gotoPage}
        onAddBookmark={addBookmark}
        onRemoveBookmark={removeBookmark}
      />

      <BacklogDrawer
        isOpen={state.isBacklogOpen}
        currentPage={state.currentPage}
        backlog={state.backlog}
        onClose={() => toggleBacklog(false)}
        onJumpToPage={gotoPage}
        onClearBacklog={clearBacklog}
      />

      <SettingsModal
        isOpen={state.isSettingsOpen}
        settings={state.settings}
        onClose={() => toggleSettings(false)}
        onUpdateSettings={updateSettings}
        onResetAllData={resetAllData}
      />

      {/* Optional CRT scanlines layer */}
      {(state.settings.crtScanlines ?? true) && <div className="crt-overlay" />}
    </div>
  );
};
